import { Table } from "flowbite-react";
import { SearchInput } from "../../SearchInput";
import * as React from "react";
import { ItemInput } from "../../../../@types";
import { useEffect, useState } from "react";
import {
  CloseIcon,
  CloseIconSmall,
  RemoveIcon,
  SearchIcon,
} from "../../../../assets/icons";
import { TableColumn } from ".";
import { useInventorySearch } from "../../../../hooks/inventory";
import { DropdownComponent } from "../../../core/Forms";
import { VariationItem } from "../../../../@types/inventory";
import { useTranslation } from "react-i18next";
import { calculateAdjustedRate } from "../../../../utils";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import { showToast } from "../../../../utils/toastUtils";
import {
  addCommasToNumber,
  addCommasToNumberFixed,
  removeCommasFromNumber,
} from "../../../../utils/convertToFloat";

export interface Props {
  item: ItemInput;
  columns: TableColumn[];
  index: number;
  isSearchInput: boolean;
  removeItem: (index: number) => void;
  updateError: (index: number, error: string[]) => void;
  updateCell: (
    rowIndex: number,
    columnId: string,
    value: string | number,
  ) => void;
  updateRow: (rowIndex: number, updatedRow: any) => void;
  errors: string[];
  onUpdateVariation?: (id: number, field: string, value: string) => void;
  variationList?: any;
  variations?: VariationItem[];
  isInvoice: boolean;
  handleClearErrors: () => void;
  isSidebarCollapsed?: boolean;
  exchangeRate?: number;
  currency: string;
  setErrors: React.Dispatch<React.SetStateAction<string[][]>>;
  getExistingVariationTypes: (item: any) => any[];
}
export interface LineItemRef {
  validate: () => boolean;
}
let originalQuantity: number = 0;

const LineItem: React.ForwardRefRenderFunction<LineItemRef, Props> = (
  {
    isSearchInput,
    columns,
    index,
    removeItem,
    updateRow,
    updateCell,
    item,
    errors,
    updateError,
    onUpdateVariation,
    isInvoice,
    handleClearErrors,
    isSidebarCollapsed,
    exchangeRate,
    currency,
    setErrors,
    getExistingVariationTypes,
  },
  ref,
) => {
  const { t } = useTranslation("invoices");
  const [quantityValue, setQuantityValue] = React.useState<number | undefined>(
    undefined,
  );
  const [selectedVariation, setSelectedVariation] = React.useState<string>("");
  const [totalMaxQuant, setTotalMaxQuant] = React.useState("");

  const originalQuantityRef = React.useRef<number>(0);

  useEffect(() => {
    if (item && item?.variations && item?.variations?.length > 0) {
      originalQuantityRef.current = item?.variations.find(
        (i) => i.type == selectedVariation,
      )?.quantity as number;
    } else {
      originalQuantityRef.current = item?.originalQuantity as number;
    }
  }, [item, selectedVariation]);

  const handleQuantityChange = (
    field: keyof ItemInput,
    value: number | string,
  ) => {
    const quantityValue = Number(value);
    const originalQuantity = originalQuantityRef.current;
    if (quantityValue > originalQuantity) {
      showToast(
        `You entered more than the available quantity (${originalQuantity})`,
        "info",
      );
      setTotalMaxQuant(
        `You entered more than the available quantity ${originalQuantity}`,
      );
      setTimeout(() => {
        setTotalMaxQuant(``);
      }, 4000);
      setQuantityValue(originalQuantity);
    } else {
      // Remove quantity error when valid input is provided
      const newErrors = errors.filter((err) => err !== "quantity");
      updateError(index, newErrors);
      setQuantityValue(quantityValue);
    }

    updateCell(index, field as string, value);
  };

  useEffect(() => {
    if (item && item?.variations && item?.variations?.length > 0) {
      setQuantityValue(undefined);
    } else if (item && item?.name) {
      setQuantityValue(1);
    }
  }, [item]);

  const handleUpdate = (
    field: keyof ItemInput,
    value: string | number,
    label: string,
  ) => {
    updateCell(index, field as string, value);

    if (field === "rate") {
      setSelectedVariation("");
    }

    if (errors?.length > 0) {
      // Remove the error from the specific field
      setErrors((prevErrors) => {
        const newErrors = errors.filter((err) => err !== field);
        const newError = [...prevErrors];
        newError[index] = newError[index].filter((e) => newErrors.includes(e));
        return newError;
      });
      // updateError(index, newErrors);
    }

    if (field === "variations") {
      validateVariation();
    }
  };

  const {
    data: inventories,
    isSearching: isInventorySearching,
    handleSearch: handleInventorySearch,
  } = useInventorySearch();

  const [variationError, setVariationError] = React.useState<string>("");
  const [selectedInventory, setSelectedInventory] = React.useState<any>({});

  const handleInventorySearchDebounced = React.useCallback(
    handleInventorySearch,
    [],
  );

  const variationTypesOptions = () => {
    const data = getExistingVariationTypes(item);
    if (!item?.variations) return [];

    return item.variations
      .filter(
        // @ts-ignore
        (variation) => !data?.includes(variation.type),
      )
      .map((variation) => ({
        value: variation.type,
        label: variation.type,
      }));
  };

  const [selectedItem, setSelectedItem] = React.useState<ItemInput | null>(
    null,
  );
  const [selectedVariationType, setSelectedVariationType] =
    React.useState<string>("");
  const [adjustedRate, setAdjustedRate] = React.useState<number>(0);

  const handleItemSelect = React.useCallback(
    (itemData: ItemInput | null, selectedVariationType: string) => {
      if (itemData) {
        if (itemData.stockStatus !== "out-of-stock") {
          if (!itemData.isArchived) {
            const { variations, ...rest } = itemData;

            const data = getExistingVariationTypes(itemData);

            // Filter out variations that are already selected
            const availableVariations = variations?.filter(
              (v) => !data.includes(v.type),
            );

            if (
              variations &&
              variations.length > 0 &&
              availableVariations?.length == 0
            ) {
              removeItem(index);
              showToast("No more variations to select", "error");
              return;
            }

            const selectedVariationObject = availableVariations?.find(
              (v: { type: string }) => v.type === selectedVariationType,
            );

            if (selectedVariationObject) {
              originalQuantity = selectedVariationObject.quantity || 0;
            } else {
              originalQuantity = itemData.quantity || 0;
            }

            const selectedVariationQuantity =
              selectedVariationObject?.quantity || 0;
            const adjustedRate = calculateAdjustedRate(
              selectedVariationObject?.price || itemData.rate || 0,
              exchangeRate,
            );

            const updatedRow: any = {
              ...rest,
              quantity: 1,
              rate: selectedVariationObject
                ? selectedVariationObject.price
                : itemData.rate || 0,
              variation: selectedVariationObject || undefined,
            };
            const updatedRoww =
              itemData.variations && itemData.variations?.length > 0
                ? {
                    ...rest,
                    id: itemData.id,
                    name: itemData.name,
                    variations: itemData.variations,
                    quantity: 1,
                    rate: selectedVariationObject?.price,
                    variation: {
                      id: selectedVariationObject?.id,
                      type: selectedVariationObject?.type,
                      quantity: selectedVariationObject?.quantity,
                      rate: selectedVariationObject?.price,
                      originalQuantity: selectedVariationObject?.quantity,
                    },
                    originalQuantity:
                      selectedVariationObject?.quantity ?? itemData.quantity,
                  }
                : {
                    ...rest,
                    name: itemData.name,
                    id: itemData.id,
                    quantity: 1,
                    rate: itemData.rate,
                    originalQuantity: itemData.quantity,
                  };

            if (onUpdateVariation && selectedVariationObject) {
              onUpdateVariation(
                index,
                "price",
                String(selectedVariationObject.price),
              );
            }

            updateRow(index, updatedRoww);
            setVariationError("");
          } else {
            setVariationError("Item is archived");
          }
        } else {
          setVariationError("Item is out of stock");
        }
      } else {
        updateRow(index, { id: "", name: "", quantity: 0, rate: 0 });
        setVariationError("");
      }
    },
    [index, updateRow, onUpdateVariation, quantityValue, exchangeRate],
  );

  const handleSearchInputSelect = (item: ItemInput | null) => {
    handleClearErrors();
    const defaultVariationType = "DefaultType";
    handleItemSelect(item, defaultVariationType);
  };

  const validateVariation = () => {
    if (Array.isArray(item.variations) && item.variations.length > 0) {
      if (selectedVariation === "") {
        setVariationError("Please select a variation");
      }
      return selectedVariation !== "";
    }
    return true;
  };

  const inputWidth = isSidebarCollapsed
    ? "w-full md:w-[120px] lg:w-[120px]"
    : "w-full md:w-[170px] lg:w-[250px]";
  return (
    <>
      <Table.Row key={index} className=" no-border">
        {columns.map(({ label }, columnIndex) => (
          <Table.Cell key={columnIndex} className="pb-0">
            {columnIndex === 0 ? (
              isSearchInput ? (
                <div className="mt-[-2.9em] relative w-full xl:w-[370px]">
                  <SearchInput
                    placeholder="Enter Item Name"
                    data={inventories}
                    displayKey="name"
                    isLoading={isInventorySearching}
                    emptyMessage={"No Items yet"}
                    classNames={`w-full placeholder:text-[#B4B4B4] placeholder:text-[13.25px] bg-white w-full  border rounded-md placeholder:text-[#A4A4A4] ${
                      variationError || errors.includes("name")
                        ? "border-red-500"
                        : "border-[#A4A4A4]"
                    }  ${inputWidth}`}
                    style={{ width: "100%" }}
                    inputStyleMain={{ width: "100%" }}
                    value={item?.name || ""}
                    selected={item.id ? item : null}
                    clearIcon={CloseIconSmall}
                    handleChangeText={handleInventorySearchDebounced}
                    onSelect={handleSearchInputSelect}
                    dropdownClassName="xl:w-[310px] lg:w-[250px] 2xl:w-[370px]  md:w-[170px]  biglg:w-[350px] bigmd:w-[160px] 3xl:w-[370px] 4xl:w-[320px] lg:[120px] "
                    dropdownStyle={{
                      maxHeight: "800px",
                      overflowY: "auto",
                    }}
                    searchKey={"name"}
                    icon={<SearchIcon />}
                  />

                  {variationError ? (
                    <p className="absolute text-red-500 text-[11px]">
                      {variationError}
                    </p>
                  ) : errors.includes("name") ? (
                    <p className="absolute text-red-500 text-[11px]">
                      Item Name is required
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="relative">
                  <div
                    className={`xl:w-[350px] w-full ${isSidebarCollapsed ? "w-[200px]" : ""}`}
                  >
                    <input
                      type="text"
                      className={`placeholder:text-[13.25px] bg-white placeholder:text-[#B4B4B4] rounded-md border border-[#A4A4A4] w-full md:w-[100%] lg:w-[250px] biglg:w-[100%] xl:w-[100%] 2xl:w-[100%] 3xl:w-[100%] bigmd:w-[100%] 4xl:w-[100%] focus:outline-none focus:shadow-outline appearance-none focus:ring-0" ${
                        errors.includes("name") ? "border-red-500" : ""
                      } ${
                        item?.name
                          ? "focus:outline-none focus:border-blue-600 focus:ring-0 focus:ring-blue-600"
                          : ""
                      }`}
                      placeholder="Enter Item Name"
                      value={item?.name}
                      onChange={({ target }) =>
                        handleUpdate("name", target.value, label)
                      }
                    />
                  </div>
                  {errors.includes("name") && (
                    <p className="absolute text-red-500 text-[11px]">
                      Item Name is required
                    </p>
                  )}
                </div>
              )
            ) : columnIndex === 1 && isInvoice ? (
              <div className="mb-4 max-w-[200px]">
                <DropdownComponent
                  options={variationTypesOptions()}
                  value={selectedVariation}
                  onChange={(name, value) => {
                    handleItemSelect(item, value);
                  }}
                  selectedValue={selectedVariation}
                  setSelectedValue={setSelectedVariation}
                  placeholder={
                    item &&
                    item.id &&
                    (!Array.isArray(item.variations) ||
                      item.variations.length === 0)
                      ? "No variations"
                      : "Select"
                  }
                  name="type"
                  variationIndex={index}
                  height="h-[44px] rounded-md"
                  iconClassName=""
                  disabled={
                    item &&
                    item.id &&
                    (!Array.isArray(item.variations) ||
                      item.variations.length === 0)
                      ? true
                      : false
                  }
                />
                <div
                  className="text-white text-xs mt-[1em]"
                  style={{
                    visibility:
                      item &&
                      item.id &&
                      (!Array.isArray(item.variations) ||
                        item.variations.length === 0)
                        ? "visible"
                        : "hidden",
                  }}
                >
                  No variations
                </div>
              </div>
            ) : columnIndex === columns.length - 2 && isInvoice ? (
              <div className="mt-[-3em]">
                <input
                  type="text"
                  className="placeholder:text-[13.25px] md:w-[100px] bigmd:w-[77px]  lg:max-h-[44px] lg:min-h-[44px] md:max-h-[48px] md:min-h-[48px] max-h-[48px] lg:w-[150px] biglg:w-[110px] 2xl:w-[160px] 3xl:w-[210px] 4xl:w-[300px] xl:w-[150px] bg-white placeholder:text-[#B4B4B4] rounded-md border border-[#A4A4A4] focus:outline-none focus:shadow-outline appearance-none focus:ring-0 EXISTING "
                  readOnly
                  disabled
                  placeholder="Amount"
                  value={
                    !isNaN(Number(quantityValue)) && !isNaN(Number(item?.rate))
                      ? Number(quantityValue) *
                          Number(
                            exchangeRate
                              ? (item?.rate ?? 0) / (exchangeRate ?? 1)
                              : item?.rate,
                          ) &&
                        addCommasToNumber(
                          Number(quantityValue) *
                            Number(
                              exchangeRate
                                ? (item?.rate ?? 0) / (exchangeRate ?? 1)
                                : item?.rate,
                            ),
                        )
                      : ""
                  }
                />
              </div>
            ) : columnIndex === columns.length - 2 ? (
              <div>
                <input
                  type="text"
                  className="placeholder:text-[13.25px] md:w-[100px] bigmd:w-[100px] lg:w-[150px] biglg:w-[160px] 2xl:w-[300px]  xl:w-[220px] bg-white placeholder:text-[#B4B4B4] rounded-md border border-[#A4A4A4] "
                  readOnly
                  placeholder="Amount"
                  value={
                    !isNaN(Number(item?.quantity)) && !isNaN(Number(item?.rate))
                      ? addCommasToNumber(
                          Number(item?.quantity) * Number(item?.rate),
                        )
                      : ""
                  }
                />
              </div>
            ) : columnIndex === columns.length - 4 && isInvoice ? (
              <div
                className="mt-[-3em] w-full"
                style={{ position: "relative" }}
              >
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={` bg-white placeholder:text-[#B4B4B4] lg:max-h-[44px] lg:min-h-[44px] md:max-h-[48px] md:min-h-[48px] max-h-[48px] rounded-md border border-[#A4A4A4]  placeholder:text-[13.25px] md:w-[70px] bigmd:w-[77px] lg:w-[150px] biglg:w-[110px] xl:w-[150px] 2xl:w-[160px] 3xl:w-[210px] 4xl:w-[300px] focus:outline-none focus:shadow-outline appearance-none focus:ring-0"
                  placeholder="Quantity ${
                    !variationError && errors.includes("quantity")
                      ? "border-red-500"
                      : ""
                  }`}
                  value={quantityValue}
                  onChange={({ target }) => {
                    const newValue = Number(target.value);
                    setQuantityValue(newValue ?? 0);
                    handleQuantityChange("quantity", newValue ?? 0);
                    handleUpdate(
                      label.toLowerCase() as keyof ItemInput,
                      newValue,
                      label,
                    );
                  }}
                />
                {/* {quantityValue !== undefined && quantityValue > originalQuantity && (
                <div style={{ position: 'absolute', top: '100%', left: 0, color: 'red', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  Typed quantity exceeds original quantity
                </div>
              )} */}
                {!variationError && errors.includes("quantity") && (
                  <p className="absolute text-red-500 text-[11px]">
                    Quantity is required
                  </p>
                )}
                {totalMaxQuant && (
                  <p className="absolute text-red-500 text-[11px]">
                    {totalMaxQuant}
                  </p>
                )}
              </div>
            ) : columnIndex === columns.length - 4 ? (
              <div className="relative">
                <div>
                  <input
                    type="text"
                    min="0"
                    step="0.01"
                    className={`bg-white placeholder:text-[#B4B4B4]  lg:max-h-[44px] lg:min-h-[44px] md:max-h-[48px] md:min-h-[48px] max-h-[48px] rounded-md border border-[#A4A4A4]  placeholder:text-[13.25px] md:w-[100px] bigmd:w-[100px] lg:w-[150px] biglg:w-[160px] xl:w-[220px] 2xl:w-[200px] 3xl:w-[210px] 4xl:w-[300px] FREEFORM ${
                      !variationError && errors.includes("quantity")
                        ? "border-red-500"
                        : ""
                    }`}
                    placeholder={
                      label.toLowerCase() === "quantity" ? " Quantity" : " Rate"
                    }
                    value={
                      Number(item?.[label.toLowerCase()])
                        ? addCommasToNumber(
                            Number(item?.[label.toLowerCase()] as number),
                          )
                        : ""
                    }
                    onChange={({ target }) => {
                      const newValue = target.value;

                      handleUpdate(
                        label.toLowerCase() as keyof ItemInput,
                        removeCommasFromNumber(newValue || "0"),
                        label,
                      );
                    }}
                  />
                </div>
                {errors.includes("quantity") && (
                  <p className="absolute text-red-500 text-[11px]">
                    Quantity is required
                  </p>
                )}
              </div>
            ) : columnIndex === columns.length - 1 && isInvoice ? (
              <div className="mt-[-2.5em]">
                {index !== 0 && (
                  <button type="button" onClick={() => removeItem(index)}>
                    <DeleteIcon /> <span className="text-xl text-red "></span>
                  </button>
                )}
              </div>
            ) : columnIndex === columns.length - 1 ? (
              <div className="mt-[0.5em]">
                {index !== 0 && (
                  <button type="button" onClick={() => removeItem(index)}>
                    <DeleteIcon /> <span className="text-xl text-red "></span>
                  </button>
                )}
              </div>
            ) : columnIndex === columns.length - 3 && isInvoice ? (
              <div className="mt-[-3em]">
                <input
                  type="text"
                  min="0"
                  step="0.01"
                  readOnly
                  disabled
                  className={`bg-white placeholder:text-[#B4B4B4]  lg:max-h-[44px] lg:min-h-[44px] md:max-h-[48px] md:min-h-[48px] max-h-[48px] rounded-md border border-[#A4A4A4]  placeholder:text-[13.25px] md:w-[100px] bigmd:w-[100px] lg:w-[150px] biglg:w-[150px] xl:w-[220px] 2xl:w-[160px] 3xl:w-[210px] 4xl:w-[300px]  ${
                    !variationError && errors.includes("rate")
                      ? "border-red-500"
                      : ""
                  }`}
                  placeholder="Rate"
                  value={
                    exchangeRate
                      ? addCommasToNumberFixed(
                          (item?.rate ?? 0) / (exchangeRate ?? 1),
                        )
                      : item?.rate
                  }
                  onChange={({ target }) => {
                    handleUpdate(
                      "rate",
                      parseFloat(removeCommasFromNumber(target.value) || "0"),
                      label.toLowerCase(),
                    );
                  }}
                />
                {!variationError && errors.includes("rate") && (
                  <p className="absolute text-red-500 text-[11px]">
                    Rate is required
                  </p>
                )}
              </div>
            ) : columnIndex === columns.length - 3 ? (
              <div className="relative">
                <div className="mt-[em]">
                  <input
                    type="text"
                    className={`bg-white placeholder:text-[#B4B4B4]  lg:max-h-[44px] lg:min-h-[44px] md:max-h-[48px] md:min-h-[48px] max-h-[48px] rounded-md border border-[#A4A4A4]  placeholder:text-[13.25px] md:w-[100px] bigmd:w-[100px] lg:w-[150px] biglg:w-[150px] xl:w-[220px] 2xl:w-[160px] 3xl:w-[210px] 4xl:w-[300px] FREEFORM ${
                      errors.includes("rate") ? "border-red-500" : ""
                    }`}
                    placeholder="Rate"
                    value={addCommasToNumber(item?.rate || 0)}
                    onChange={({ target }) =>
                      handleUpdate(
                        "rate",
                        parseFloat(removeCommasFromNumber(target.value) || "0"),
                        label.toLowerCase(),
                      )
                    }
                  />
                </div>
                {errors.includes("rate") && (
                  <p className="absolute text-red-500 text-[11px]">
                    Rate is required
                  </p>
                )}
              </div>
            ) : null}
          </Table.Cell>
        ))}
      </Table.Row>
    </>
  );
};
export default React.forwardRef(LineItem);
